import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax

import { useQuery } from '@tanstack/react-query';

import SoftBox from 'components/SoftComponents/SoftBox';
import SoftTypography from 'components/SoftComponents/SoftTypography';

import { getAllDevices } from 'services/api/bks';

import 'mapbox-gl/dist/mapbox-gl.css';

// props-types is a library for typechecking of props
import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Box from '@mui/material/Box';

import { center, bbox, bboxPolygon } from "@turf/turf";
import geoViewport from "@mapbox/geo-viewport";
import { useNavigate } from 'react-router-dom';

import cameraOnline from 'assets/images/camera_online.png';
import cameraWarning from 'assets/images/camera_warning.png';
import cameraOffline from 'assets/images/camera_offline.png';
import { getUserAccessLevel } from 'utils/setup';
import { setGroupPolygonColour, setDevicePointColour, setCameraDeviceIcon } from 'utils/map';

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

function setGroupFeatures(group, user_type) {
    const { geojson, ...metadata } = group;

    var group_features = geojson;

    group_features.forEach((feature) => {
        feature.properties = {
            ...metadata,
            access: getUserAccessLevel(user_type, 'technician'),
            feature_colour: setGroupPolygonColour(group, user_type)
        };
    });

    return group_features;
}

function setDeviceFeatures(device, user_type) {
    var ret = {
        type: "Feature",
        properties: {
            ...device,
            access: getUserAccessLevel(user_type, 'technician'),
            ...setDevicePointColour(device, user_type)
        },
        geometry: {
            type: 'Point',
            coordinates: device.position
        }
    }

    if (device.hardware_type == "bk-camera") {
        ret.properties.icon = setCameraDeviceIcon(device, user_type);
    }

    return ret;
}

function ClearwaysDevicesMap({ map, user, groups_data, showDevices = null }) {

    const navigate = useNavigate();
    const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false
    });

    const handleGroupClick = (e) => {
        if (!e.defaultPrevented) {
            e.preventDefault();
            if (groups_data.parent_group) {
                popup.remove();
                navigate("/overview/" + groups_data.parent_group.group_id);
            }
        }
    }

    const handleDeviceClick = (e) => {
        e.preventDefault();
        popup.remove();
        navigate("/devices/" + e.features[0].properties.uuid);
    }

    const handleMapClick = (e) => {
        if (!e.defaultPrevented) {
            popup.remove();
            navigate("/overview");
        }
    }

    const handleDeviceMouseEnter = (e) => {
        map.current.getCanvas().style.cursor = 'pointer';

        const coordinates = e.features[0].geometry.coordinates.slice();
        var description = `<div style='text-align:center; font-size:16px'><b>${e.features[0].properties.site_id}</b></div>`;
        if (getUserAccessLevel(user.user.user_type, 'technician')) {
            description = `<div style='text-align:center; font-size:16px'><b>${e.features[0].properties.site_id}</b><br><div style='font-size:13px'>${e.features[0].properties.name}</div></div>`;
        }

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        popup.setLngLat(coordinates).setHTML(description).addTo(map.current);
    }

    const handleDeviceMouseLeave = (e) => {
        map.current.getCanvas().style.cursor = '';
        popup.remove();
    }

    useEffect(() => {
        if (!map.current) return;

        if (!map.current.hasImage('cameraIconOnline')) {
            map.current.loadImage(cameraOnline, (error, image) => {
                if (error) throw error;
                if (!map.current.hasImage('cameraIconOnline')) {
                    map.current.addImage('cameraIconOnline', image);
                }
            });
        }

        if (!map.current.hasImage('cameraIconWarning')) {
            map.current.loadImage(cameraWarning, (error, image) => {
                if (error) throw error;
                if (!map.current.hasImage('cameraIconWarning')) {
                    map.current.addImage('cameraIconWarning', image);
                }
            });
        }

        if (!map.current.hasImage('cameraIconOffline')) {
            map.current.loadImage(cameraOffline, (error, image) => {
                if (error) throw error;
                if (!map.current.hasImage('cameraIconOffline')) {
                    map.current.addImage('cameraIconOffline', image);
                }
            });
        }

        if (!Array.isArray(groups_data.parent_group)) {
            var existing_base_layer = map.current.getLayer('groups-layer');
            var existing_base_source = map.current.getSource('groups-source');

            if (!existing_base_source) {
                map.current.addSource('groups-source', {
                    type: 'geojson',
                    data: {
                        "type": "FeatureCollection",
                        "features": setGroupFeatures(groups_data.parent_group, user.user.user_type)
                    }
                });
            }
            if (!existing_base_layer) {
                map.current.addLayer({
                    id: 'groups-layer',
                    type: 'fill',
                    source: 'groups-source',
                    paint: {
                        "fill-opacity": 0.1,
                        "fill-color": "white"
                    }
                }).addLayer({
                    id: 'groups-layer-liner',
                    type: 'line',
                    source: 'groups-source',
                    paint: {
                        "line-color": "white",
                        "line-width": 2,
                        "line-opacity": 0.2
                    }
                });
            }

            // handlers here
        }

        if (groups_data.siblings.length > 0) {
            var existing_siblings_layer = map.current.getLayer('siblings-layer');
            var existing_siblings_source = map.current.getSource('siblings-source');

            if (!existing_siblings_source) {
                map.current.addSource('siblings-source', {
                    type: 'geojson',
                    data: {
                        "type": "FeatureCollection",
                        "features": groups_data.siblings.map(sibling => setGroupFeatures(sibling, user.user.user_type))
                    }
                });
            }
            if (!existing_siblings_layer) {
                map.current.addLayer({
                    id: 'siblings-layer',
                    type: 'line',
                    source: 'siblings-source',
                    paint: {
                        "line-color": "white",
                        "line-width": 5,
                        "line-opacity": 0.4
                    }
                });
            }

            // handlers here
        }

        var existing_group_source = map.current.getSource('subgroup-source');
        var existing_group_layer = map.current.getLayer('subgroup-layer');

        if (!existing_group_source) {
            map.current.addSource('subgroup-source', {
                type: 'geojson',
                data: {
                    "type": "FeatureCollection",
                    "features": setGroupFeatures(groups_data, user.user.user_type)
                }
            });
        }

        if (!existing_group_layer) {
            if (!Array.isArray(groups_data.parent_group)) {
                map.current.addLayer({
                    id: 'subgroup-layer',
                    type: 'line',
                    source: 'subgroup-source',
                    paint: {
                        "line-color": ['get', 'feature_colour'],
                        "line-width": 5,
                    }
                });
            }
            else {
                map.current.addLayer({
                    id: 'subgroup-layer',
                    type: 'fill',
                    source: 'subgroup-source',
                    paint: {
                        "fill-opacity": 0.1,
                        "fill-color": "white"
                    }
                }).addLayer({
                    id: 'subgroup-layer-liner',
                    type: 'line',
                    source: 'subgroup-source',
                    paint: {
                        "line-color": "white",
                        "line-width": 2,
                        "line-opacity": 0.2
                    }
                });
            }
        }

        var existing_camera_device_layer = map.current.getLayer('device-img-layer');
        var existing_camera_device_source = map.current.getSource('device-img-source');

        if (!existing_camera_device_source) {
            map.current.addSource('device-img-source', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: groups_data.devices.filter(device => device.hardware_type == "bk-camera").map(device => setDeviceFeatures(device, user.user.user_type)).sort((a, b) => {
                        return a.properties.zIndex - b.properties.zIndex
                    })
                }
            });
        }

        if (!existing_camera_device_layer) {
            map.current.addLayer({
                id: 'device-img-layer',
                type: 'symbol',
                source: 'device-img-source',
                layout: {
                    'icon-image': ['get', 'icon'],
                    'icon-size': 0.044
                }
            });
        }

        var existing_device_layer = map.current.getLayer('device-layer');
        var existing_device_source = map.current.getSource('device-source');

        if (!existing_device_source) {
            map.current.addSource('device-source', {
                type: 'geojson',
                data: {
                    "type": "FeatureCollection",
                    "features": groups_data.devices.filter(device => device.hardware_type != "bk-camera").map(device => setDeviceFeatures(device, user.user.user_type)).sort((a, b) => {
                        return a.properties.zIndex - b.properties.zIndex
                    })
                }
            });
        }
        if (!existing_device_layer) {
            map.current.addLayer({
                id: 'device-layer',
                type: 'circle',
                source: 'device-source',
                paint: {
                    'circle-color': ['get', 'feature_colour'],
                    'circle-radius': 9,
                    'circle-stroke-width': 3,
                    'circle-stroke-color': '#ffffff'
                }
            });
        }

        map.current.on('click', 'device-img-layer', handleDeviceClick);
        map.current.on('mouseenter', 'device-img-layer', handleDeviceMouseEnter);
        map.current.on('mouseleave', 'device-img-layer', handleDeviceMouseLeave);

        map.current.on('click', 'device-layer', handleDeviceClick);
        map.current.on('mouseenter', 'device-layer', handleDeviceMouseEnter);
        map.current.on('mouseleave', 'device-layer', handleDeviceMouseLeave);

        map.current.on('click', 'groups-layer', handleGroupClick);
        map.current.on('click', handleMapClick);

        return () => {
            map.current.off('click', 'device-img-layer', handleDeviceClick);
            map.current.off('mouseenter', 'device-img-layer', handleDeviceMouseEnter);
            map.current.off('mouseleave', 'device-img-layer', handleDeviceMouseLeave);

            map.current.off('click', 'device-layer', handleDeviceClick);
            map.current.off('mouseenter', 'device-layer', handleDeviceMouseEnter);
            map.current.off('mouseleave', 'device-layer', handleDeviceMouseLeave);


            map.current.off('click', 'groups-layer', handleGroupClick);
            map.current.off('click', handleMapClick);
        }

    }, [showDevices, groups_data]);

    useEffect(() => () => { //unmount

        map.current.removeImage('cameraIconOnline');
        map.current.removeImage('cameraIconWarning');
        map.current.removeImage('cameraIconOffline');

        if (!Array.isArray(groups_data.parent_group)) {
            map.current.removeLayer('groups-layer-liner');
            map.current.removeLayer('groups-layer');
            map.current.removeSource('groups-source');
        }

        if (groups_data.subgroups.length > 0) {
            map.current.removeLayer('siblings-layer');
            map.current.removeSource('siblings-source');
        }

        if (Array.isArray(groups_data.parent_group)) {
            map.current.removeLayer('subgroup-layer-liner');
        }
        map.current.removeLayer('subgroup-layer');
        map.current.removeSource('subgroup-source');

        map.current.removeLayer('device-img-layer');
        map.current.removeSource('device-img-source');

        map.current.removeLayer('device-layer');
        map.current.removeSource('device-source');

        map.current.getCanvas().style.cursor = '';
        popup.remove();

    }, []);
}

ClearwaysDevicesMap.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object,
    groups_data: PropTypes.array,
    showDevices: PropTypes.any
};

export default ClearwaysDevicesMap;